<script setup lang="ts">
/**
 * design-system.md -> Rail, mail-admin-handoff-v2-changes/CHANGES.md §1 -> Rail,
 * component-sidebar.html. Fixed 248px, always dark — `on-dark` on the root swaps the focus ring to
 * lime (see main.css). Brand -> Find tenant field -> primary nav (Tenants/Team) -> Recent (max 5)
 * -> signed-in user block pinned to the bottom. v2 replaced the old "TENANTS" overline + "+" +
 * full tenant list with the Find tenant field and the bounded Recent section below — the rail never
 * grows with the number of tenants any more.
 *
 * Active-state rules (design-system.md -> Rail -> "Active item" + 03-app-shell.md):
 *  - Tenants gets the rail-active fill only on the /tenants index. On a tenant page it's white
 *    text with no fill — the fill moves to that tenant's own Recent row instead, if it's listed.
 *  - The active tenant's Tile turns "signal" (lime, ink text); every other tenant Tile is "rail".
 *  - Team / Unrouted each get the fill only on their own route.
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { findTenantOpen, isAdmin, me, tenants, users } from '@/session';
import { recentTenants } from '@/recentTenants';
import BrandMark from '@/components/ui/BrandMark.vue';
import Tile from '@/components/ui/Tile.vue';
import Avatar from '@/components/ui/Avatar.vue';
import { ArrowRight, Inbox, LogOut, Search } from '@lucide/vue';

const route = useRoute();

const onTenantPage = computed(() => typeof route.params.slug === 'string');
const activeTenantSlug = computed(() => (onTenantPage.value ? (route.params.slug as string) : null));

const tenantsNavClass = computed(() => {
  if (route.name === 'tenants') return 'bg-rail-active text-on-ink';
  if (onTenantPage.value) return 'text-on-ink';
  return 'text-rail-text';
});

// navigator.userAgentData is Chromium-only — .platform is the universal fallback (deprecated but
// still populated everywhere, including Firefox/Safari, which never shipped userAgentData at all).
const keyChipLabel = computed(() => {
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  const platform = uaData?.platform ?? navigator.platform ?? '';
  return /mac/i.test(platform) ? '⌘K' : 'Ctrl K';
});
</script>

<template>
  <nav aria-label="Main" class="on-dark flex h-screen w-[248px] shrink-0 flex-col bg-rail px-3.5 pt-[22px] pb-4 text-rail-text">
    <!-- Brand -->
    <div class="flex h-9 shrink-0 items-center gap-2.5 px-2">
      <BrandMark :size="28" />
      <span class="type-wordmark text-on-ink">PostNoor Admin</span>
    </div>

    <!-- Find tenant field. 24px below the brand; opens the palette (AppShell also binds ⌘K/Ctrl+K
         globally to the same session.findTenantOpen ref). -->
    <button
      type="button"
      aria-keyshortcuts="Meta+K Control+K"
      class="mt-6 flex h-10 shrink-0 items-center gap-2.5 rounded-control border border-rail-tile bg-rail-field pr-2 pl-2.5 text-[13.5px] text-rail-muted"
      @click="findTenantOpen = true"
    >
      <Search :size="16" :stroke-width="1.9" aria-hidden="true" />
      <span class="grow text-left">Find tenant</span>
      <span aria-hidden="true" class="flex h-[22px] items-center rounded-[5px] border border-rail-tile px-1.5 font-mono text-[11px] text-rail-muted">
        {{ keyChipLabel }}
      </span>
    </button>

    <!-- Primary nav. 20px below the field (was 32px below the brand in v1). -->
    <div class="mt-5 flex shrink-0 flex-col gap-0.5">
      <RouterLink
        :to="{ name: 'tenants' }"
        :aria-current="route.name === 'tenants' ? 'page' : undefined"
        class="flex h-10 items-center gap-3 rounded-control px-2.5 type-control no-underline"
        :class="tenantsNavClass"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 4 3 9l9 5 9-5-9-5Z" />
          <path d="m3 14 9 5 9-5" />
        </svg>
        <span class="grow">Tenants</span>
        <span class="font-mono text-[11px] text-rail-muted">{{ tenants.length }}</span>
      </RouterLink>

      <RouterLink
        v-if="isAdmin"
        :to="{ name: 'team' }"
        :aria-current="route.name === 'team' ? 'page' : undefined"
        class="flex h-10 items-center gap-3 rounded-control px-2.5 type-control no-underline"
        :class="route.name === 'team' ? 'bg-rail-active text-on-ink' : 'text-rail-text'"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2.5 20c.7-3.5 3.3-5.5 6.5-5.5s5.8 2 6.5 5.5" />
          <path d="M16 4.7a3.5 3.5 0 0 1 0 6.6" />
          <path d="M18 14.7c1.9.7 3.1 2.4 3.5 5.3" />
        </svg>
        <span class="grow">Team</span>
        <span class="font-mono text-[11px] text-rail-muted">{{ users?.length ?? 0 }}</span>
      </RouterLink>

      <!-- Deviation (see report): not on the design screens. Admin-only home for the old "Unrouted
           inbound events" section — reuses the same icon family as Webhooks (RadioTower) would be
           ambiguous, so Inbox reads better for "mail that landed nowhere". -->
      <RouterLink
        v-if="isAdmin"
        :to="{ name: 'unrouted' }"
        :aria-current="route.name === 'unrouted' ? 'page' : undefined"
        class="flex h-10 items-center gap-3 rounded-control px-2.5 type-control no-underline"
        :class="route.name === 'unrouted' ? 'bg-rail-active text-on-ink' : 'text-rail-text'"
      >
        <Inbox :size="18" :stroke-width="1.75" aria-hidden="true" />
        <span class="grow">Unrouted</span>
      </RouterLink>
    </div>

    <!-- Recent — up to 5 tenants this viewer opened most recently (recentTenants.ts), never the
         full tenant list, so the rail never grows with the tenant count. The Recent group and "All
         tenants" sit directly under the nav at their natural height; the trailing spacer (flex-1
         min-h-0, last in this wrapper) is what claims the leftover space and pins the user block to
         the bottom — it must come AFTER "All tenants", not wrap the tenant rows, or "All tenants"
         gets pushed all the way down instead of sitting right below Recent. No history -> "All
         tenants" alone, sitting where the Recent group would've started; no overline, no empty text. -->
    <div class="mt-7 flex min-h-0 flex-1 flex-col gap-0.5">
      <template v-if="recentTenants.length > 0">
        <div class="flex h-8 shrink-0 items-center px-2.5">
          <span class="type-overline text-rail-muted">Recent</span>
        </div>

        <RouterLink
          v-for="tenant in recentTenants"
          :key="tenant.slug"
          :to="{ name: 'tenant-keys', params: { slug: tenant.slug } }"
          :aria-current="activeTenantSlug === tenant.slug ? 'page' : undefined"
          class="flex h-9 shrink-0 items-center gap-2.5 rounded-control px-2.5 type-small no-underline"
          :class="activeTenantSlug === tenant.slug ? 'bg-rail-active text-on-ink' : 'text-rail-text'"
        >
          <Tile :name="tenant.name" :size="20" :tone="activeTenantSlug === tenant.slug ? 'signal' : 'rail'" />
          <span class="grow truncate">{{ tenant.name }}</span>
        </RouterLink>
      </template>

      <RouterLink :to="{ name: 'tenants' }" class="flex h-8 shrink-0 items-center gap-2 rounded-control px-2.5 type-small text-rail-muted no-underline">
        <span>All tenants</span>
        <ArrowRight :size="14" :stroke-width="1.9" aria-hidden="true" />
      </RouterLink>

      <div class="min-h-0 flex-1"></div>
    </div>

    <!-- Signed-in user -->
    <div v-if="me" class="flex shrink-0 items-center gap-2.5 border-t border-rail-divider pt-3.5 pr-1 pb-0 pl-1.5">
      <Avatar :email="me.email" :size="32" tone="rail" />
      <div class="flex min-w-0 grow flex-col gap-px">
        <span class="truncate text-[13px] text-on-ink">{{ me.email }}</span>
        <span class="text-[12px] text-rail-muted">{{ me.role }}</span>
      </div>
      <a
        href="/auth/logout"
        aria-label="Sign out"
        class="flex size-9 shrink-0 items-center justify-center rounded-control text-rail-text no-underline"
      >
        <LogOut :size="18" :stroke-width="1.75" aria-hidden="true" />
      </a>
    </div>
  </nav>
</template>
